/**
 * Utility functions for handling user avatars with fallbacks
 */

//import type { User } from '../types/user';

// Use the exact same API URL detection logic as api.ts
const getBackendUrl = (): string => {
  const hostname = window.location.hostname;
  const envApiUrl = import.meta.env.VITE_API_URL;
  
  // Check if we're in production (deployed)
  if (hostname === 'sphere.fun' || hostname === 'www.sphere.fun') {
    return 'https://api.sphere.fun';
  }
  
  // Use environment variable or fallback to localhost for development
  const apiUrl = envApiUrl || 'http://localhost:3000';
  return apiUrl;
};

/* const isFullUrl = (path: string | null | undefined): boolean => {
  return !!path && (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//'));
}; */

export const getDefaultAvatarUrl = (seed: string | null | undefined): string => {
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed || 'default'}`;
};

export const getAvatarUrl = (avatarImage: string | undefined | null, handle: string | undefined, isFullUrl: boolean = false): string => {
  const hasAvatarImage = Boolean(avatarImage && avatarImage.trim());
  
  if (hasAvatarImage && !isFullUrl) {
    // If it's already a full URL, return as is
    if (avatarImage!.startsWith('http')) {
      return avatarImage!;
    }
    // Otherwise, construct the full URL using proper backend URL
    return `${getBackendUrl()}${avatarImage}`;
  } else if (hasAvatarImage && isFullUrl) {
    // Already a full URL
    return avatarImage!;
  } else {
    // Default avatar
    const defaultAvatar = `https://api.dicebear.com/7.x/avataaars/svg?seed=${handle || 'default'}`;
    return defaultAvatar;
  }
};

// A more generic type for any object that can have an avatar
/* type Avatarable = {
  handle?: string;
  avatar?: string; // Direct avatar property
  avatarImage?: string; // For socialCard-like objects
  socialCard?: {
    avatarImage?: string | null;
  } | null;
} | null | undefined; */

/**
 * Gets the avatar URL for a user-like object.
 * This function can handle different object structures (User, Friend, SocialCardData).
 * @param entity The user, friend, or social card object.
 * @returns The full URL to the avatar image.
 */
export const getUserAvatarUrl = (user: any): string => {
  if (!user) return 'https://api.dicebear.com/7.x/avataaars/svg?seed=default';
  
  const backendUrl = getBackendUrl();
  
  // Check for socialCard.avatarImage first (preferred)
  const socialCardAvatarImage = user.socialCard?.avatarImage;
  if (socialCardAvatarImage && socialCardAvatarImage.trim()) {
    // If it's already a full URL, return as is
    if (socialCardAvatarImage.startsWith('http')) {
      return socialCardAvatarImage;
    }
    // Otherwise, construct the full URL
    return `${backendUrl}${socialCardAvatarImage}`;
  }
  
  // Fallback to user.avatarImage
  if (user.avatarImage && user.avatarImage.trim()) {
    // If it's already a full URL, return as is
    if (user.avatarImage.startsWith('http')) {
      return user.avatarImage;
    }
    // Otherwise, construct the full URL
    return `${backendUrl}${user.avatarImage}`;
  }
  
  // Final fallback to default
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.handle || 'default'}`;
};

export const getFriendAvatarUrl = getUserAvatarUrl;
export const getPostAuthorAvatarUrl = getUserAvatarUrl;


/**
 * Generates an error handler for MUI Avatar's onError prop.
 * If the provided src fails, it falls back to a default DiceBear avatar.
 * @param handle The user handle to use as a seed for the fallback avatar.
 * @returns An event handler for the onError event.
 */
export const getAvatarErrorHandler = (handle?: string | null) => (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
  const target = event.target as HTMLImageElement;
  const fallbackUrl = getDefaultAvatarUrl(handle);
  if (target.src !== fallbackUrl) {
    target.src = fallbackUrl;
  }
};

/**
 * A specific error handler for a user-like object.
 */
export const getUserAvatarErrorHandler = (entity: { handle?: string } | null | undefined) => {
  return getAvatarErrorHandler(entity?.handle);
};

export const isDefaultAvatar = (avatarUrl: string): boolean => {
  return avatarUrl.includes('api.dicebear.com');
};

export const getNotificationAvatarUrl = (notification: {
  data?: {
    fromUserAvatar?: string;
    fromUserHandle?: string;
  };
} | null | undefined): string => {
  if (!notification) {
    return getDefaultAvatarUrl('default');
  }

  if (notification.data?.fromUserAvatar && notification.data?.fromUserHandle) {
    return getAvatarUrl(notification.data.fromUserAvatar, notification.data.fromUserHandle);
  }

  return getDefaultAvatarUrl('default');
};